import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCourse } from '@/contexts/CourseContext';
import { ChevronDown, ChevronUp, FileType2, FileDown, Braces, Package, GraduationCap, LayoutDashboard, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { courseAPI } from '@/services/api';
import { STORAGE_KEYS } from '@/constants';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useCourseExports } from '@/hooks/useCourseExports';
import { capitalizeFirst } from '@/lib/utils';

/**
 * Step4Finish Component
 *
 * Supports both Start-From-Scratch and Import flows:
 * - If courseData prop is provided, can use it (Import flow)
 * - Otherwise, uses CourseContext (Start-From-Scratch flow)
 *
 * preferContextData flag:
 * - When true (Import flow), prefer the CourseContext data if it has goals,
 *   because it is guaranteed to be the freshest version after Step 3 finishes.
 *
 * @param {Object} props
 * @param {Object} props.courseData - Optional course data for Import flow
 * @param {Function} props.updateCourseData - Optional update function for Import flow
 * @param {string} props.courseId - Optional course ID for Import flow (for exports)
 * @param {boolean} props.preferContextData - Prefer context course over prop courseData when both exist
 */
export default function Step4Finish({
  courseData: propCourseData,
  updateCourseData: propUpdateCourse,
  courseId: propCourseId,
  preferContextData = false
}) {
  // Get context (for Start-From-Scratch flow and as the source of truth in import flow)
  const contextData = useCourse();
  const contextCourse = contextData?.course;
  const contextUpdateCourse = contextData?.updateCourse;

  const propHasGoals = Array.isArray(propCourseData?.learningObjectives) && propCourseData.learningObjectives.length > 0;
  const contextHasGoals = Array.isArray(contextCourse?.learningObjectives) && contextCourse.learningObjectives.length > 0;

  let course;
  let updateCourse;
  let courseId;

  // Import flow: when preferContextData is true and context has goals, always
  // prefer the context course because it's kept up-to-date after Step 3.
  if (preferContextData && contextHasGoals) {
    course = contextCourse;
    updateCourse = contextUpdateCourse;
    courseId = propCourseId || contextCourse?.id;
  } else if (propHasGoals || !contextHasGoals) {
    // Default behaviour: use prop courseData when it has goals,
    // or when context has no goals at all.
    course = propCourseData || contextCourse;
    updateCourse = propUpdateCourse || contextUpdateCourse;
    courseId = propCourseId || course?.id;
  } else {
    // Fallback: prop has no goals but context does and preferContextData is false
    course = contextCourse;
    updateCourse = contextUpdateCourse;
    courseId = propCourseId || contextCourse?.id;
  }
  const { t } = useTranslation();
  const { toast } = useToast();
  const [expandedTopics, setExpandedTopics] = useState(new Set());
  const [goalsExpanded, setGoalsExpanded] = useState(false);
  const hasInitializedTopics = useRef(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [exportsOpen, setExportsOpen] = useState(false);
  // useCourseExports needs courseId and courseData - use prop or context
  const { handleExportJson, handleExportCdm, handleExportDoc, handleExportPdf, handleExportEclass } = useCourseExports(courseId, course);

  const totalTopics = course?.topics?.length || 0;
  const totalGoals = course?.learningObjectives?.length || 0;
  const totalActivities = (course?.topics || []).reduce((sum, t) => sum + (t.activities?.length || 0), 0);

  // Initialize all topics as collapsed by default
  useEffect(() => {
    if (!course?.topics) {
      return;
    }

    if (!hasInitializedTopics.current) {
      setExpandedTopics(new Set());
      hasInitializedTopics.current = true;
    }
  }, [course?.topics]);

  const toggleTopic = (topicId) => {
    setExpandedTopics(prev => {
      const next = new Set(prev);
      if (next.has(topicId)) {
        next.delete(topicId);
      } else {
        next.add(topicId);
      }
      return next;
    });
  };

  const toggleGoals = () => {
    setGoalsExpanded(prev => !prev);
  };

  // Removed expand/collapse all controls per request

  const handleEdit = async (path, value) => {
    // path examples: "title", "description", "goals.0.text", "topics.0.activities.0.title"
    const pathParts = path.split('.');
    const updatedCourse = JSON.parse(JSON.stringify(course)); // Deep clone
    
    let current = updatedCourse;
    for (let i = 0; i < pathParts.length - 1; i++) {
      const part = pathParts[i];
      const nextPart = pathParts[i + 1];
      const index = parseInt(nextPart);
      
      if (!isNaN(index)) {
        // Next part is an index, so current[part] is an array
        current = current[part][index];
        i++; // Skip the index in next iteration
      } else {
        // Next part is a key
        current = current[part];
      }
    }
    
    // Set the final value
    const lastKey = pathParts[pathParts.length - 1];
    current[lastKey] = value;
    
    // For both flows, pass the full updated course object
    // This ensures all nested changes (goals, topics, activities) are preserved
    await updateCourse(updatedCourse);
    
    toast({
      title: t('common.updated'),
      description: t('common.changesSaved')
    });
  };

  

  return (
    <div className="h-full p-8 overflow-y-auto" data-testid="step4-finish">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white p-8 rounded-2xl shadow-xl space-y-6 mb-6" data-testid="course-overview">
          {course?.title && (
            <div>
              <h1 
                className="text-4xl font-bold text-gray-900 mb-2 hover:bg-gray-50 px-2 py-1 rounded transition-colors cursor-text"
                contentEditable
                suppressContentEditableWarning
                onBlur={(e) => handleEdit('title', e.target.textContent)}
              >
                {course.title}
              </h1>
              <div className="flex flex-wrap gap-4 mt-2">
                {course.subjectArea && <p className="text-xl text-orange-600 font-medium">{course.subjectArea}</p>}
                {course.educationalLevel && <p className="text-lg text-blue-600 font-medium">{t('progressBar.level')}: {course.educationalLevel}</p>}
                {course.grade && <p className="text-lg text-purple-600 font-medium">{t('progressBar.grade')}: {course.grade}</p>}
                {course.totalHours > 0 && (
                  <p className="text-base text-teal-600 font-medium">{t('progressBar.duration')}: {course.totalHours} {t('progressBar.hours')}</p>
                )}
              </div>
            </div>
          )}

          {course?.description && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('step4.description')}</h3>
              <p 
                className="text-gray-700 leading-relaxed hover:bg-gray-50 px-2 py-1 rounded transition-colors cursor-text"
                contentEditable
                suppressContentEditableWarning
                onBlur={(e) => handleEdit('description', e.target.textContent)}
              >
                {course.description}
              </p>
            </div>
          )}

          {course?.learningObjectives && course.learningObjectives.length > 0 && (
            <div className="border-l-4 border-orange-500 bg-orange-50 rounded-r-xl overflow-hidden shadow-sm">
              <div className="flex items-center justify-between pl-4 pr-4 py-3 hover:bg-orange-100 transition-colors">
                <h3 className="text-lg font-semibold text-gray-900">{t('step4.learningObjectives')}</h3>
                <div className="flex items-center gap-3">
                  <div 
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={toggleGoals}
                  >
                    <span className="text-sm text-orange-700 font-medium">
                      {course.learningObjectives.length} {t('step4.objectives')}
                    </span>
                    {goalsExpanded ? (
                      <ChevronUp className="text-orange-600" size={24} />
                    ) : (
                      <ChevronDown className="text-orange-600" size={24} />
                    )}
                  </div>
                </div>
              </div>
              <div className={`${goalsExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'} transition-all duration-300 overflow-hidden`}>
                <ul className="space-y-2 px-4 pb-4">
                  {course.learningObjectives.map((goal, index) => (
                    <li key={goal.id} className="flex items-start gap-3 p-3 bg-gradient-to-r from-orange-50 to-pink-50 rounded-xl">
                      <span className="flex-shrink-0 w-7 h-7 bg-gradient-to-br from-orange-500 to-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                      <span 
                        className="flex-1 text-gray-800 hover:bg-white/50 px-2 py-1 rounded transition-colors cursor-text block"
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => handleEdit(`learningObjectives.${index}.text`, e.target.textContent)}
                      >
                        {goal.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {course?.topics && course.topics.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-3">{t('step4.topicsActivities')}</h3>
              <div className="space-y-4">
                {course.topics.map((topic) => {
                  const isExpanded = expandedTopics.has(topic.id);
                  return (
                    <div key={topic.id} className="border-l-4 border-teal-500 bg-teal-50 rounded-r-xl overflow-hidden shadow-sm">
                      <div className="flex items-center justify-between pl-4 pr-4 py-4 hover:bg-teal-100 transition-colors">
                        <h4 className="font-bold text-gray-900 text-lg">{topic.title}</h4>
                        <div 
                          className="flex items-center gap-2 cursor-pointer"
                          onClick={() => toggleTopic(topic.id)}
                        >
                          <span className="text-sm text-teal-700 font-medium">
                            {topic.activities?.length || 0} {t('step4.activities')}
                          </span>
                          {isExpanded ? (
                            <ChevronUp className="text-teal-600" size={24} />
                          ) : (
                            <ChevronDown className="text-teal-600" size={24} />
                          )}
                        </div>
                      </div>
                      <div className={`${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'} transition-all duration-300 overflow-hidden`}>
                        <ul className="space-y-3 px-4 pb-4">
                      {topic.activities.map((activity, actIndex) => {
                        const topicIndex = course.topics.findIndex(t => t.id === topic.id);
                        // Support both new (objectiveId) and legacy (goalId) field names
                        const activityObjectiveId = activity.objectiveId || activity.goalId;
                        const goalIndex = course.learningObjectives?.findIndex(g => g.id === activityObjectiveId);
                        const goalText = course.learningObjectives?.find(g => g.id === activityObjectiveId)?.text;
                        return (
                          <li 
                            key={activity.id} 
                            className="bg-white p-4 rounded-xl shadow-sm"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-start gap-2 mb-2 flex-wrap">
                              <span 
                                className="text-orange-700 font-bold text-base hover:bg-orange-50 px-2 py-1 rounded transition-colors cursor-text"
                                contentEditable
                                suppressContentEditableWarning
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                onBlur={(e) => handleEdit(`topics.${topicIndex}.activities.${actIndex}.title`, e.target.textContent)}
                              >
                                {activity.title}
                              </span>
                              {typeof goalIndex === 'number' && goalIndex > -1 && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-semibold">L.O. {goalIndex + 1}</span>
                              )}
                              <span className="text-xs bg-pink-100 text-pink-700 px-2 py-1 rounded-full font-medium">
                                {capitalizeFirst(activity.actor)}
                              </span>
                              {activity.duration && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                                  {activity.duration} {t('step4.minutes', 'mins')}
                                </span>
                              )}
                            </div>
                            <p 
                              className="text-gray-700 text-sm mb-2 hover:bg-gray-50 px-2 py-1 rounded transition-colors cursor-text"
                              contentEditable
                              suppressContentEditableWarning
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                              onBlur={(e) => handleEdit(`topics.${topicIndex}.activities.${actIndex}.description`, e.target.textContent)}
                            >
                              {activity.description}
                            </p>
                            {activity.facilitatorRole && (
                              <div className="mb-2">
                                <p className="text-xs text-gray-500 font-semibold mb-1">{t('step4.facilitatorRole')}</p>
                                <p 
                                  className="text-gray-700 text-sm italic hover:bg-gray-50 px-2 py-1 rounded transition-colors cursor-text"
                                  contentEditable
                                  suppressContentEditableWarning
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => e.stopPropagation()}
                                  onBlur={(e) => handleEdit(`topics.${topicIndex}.activities.${actIndex}.facilitatorRole`, e.target.textContent)}
                                >
                                  {activity.facilitatorRole}
                                </p>
                              </div>
                            )}
                            <div className="border-t pt-2 mt-2">
                              <p className="text-xs text-gray-500 font-semibold mb-1">{t('step4.resource')}</p>
                              <div className="flex items-center flex-wrap gap-2 mb-1">
                                <span 
                                  className="text-gray-800 text-sm font-medium hover:bg-gray-50 px-2 py-1 rounded transition-colors cursor-text"
                                  contentEditable
                                  suppressContentEditableWarning
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => e.stopPropagation()}
                                  onBlur={(e) => handleEdit(`topics.${topicIndex}.activities.${actIndex}.resourceTitle`, e.target.textContent)}
                                >
                                  {activity.resourceTitle}
                                </span>
                                <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full font-medium">
                                  {activity.resourceType}
                                </span>
                              </div>
                              <p 
                                className="text-gray-600 text-sm italic hover:bg-gray-50 px-2 py-1 rounded transition-colors cursor-text"
                                contentEditable
                                suppressContentEditableWarning
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                onBlur={(e) => handleEdit(`topics.${topicIndex}.activities.${actIndex}.resourceDescription`, e.target.textContent)}
                              >
                                {activity.resourceDescription}
                              </p>
                            </div>
                          </li>
                        );
                      })}
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-4 items-center justify-center">
            <Button
              onClick={async () => {
              try {
                // Clear appropriate storage key based on flow
                if (propCourseData) {
                  // Import flow - clear import storage
                  localStorage.removeItem(STORAGE_KEYS.IMPORT_COURSE_ID);
                  localStorage.removeItem(STORAGE_KEYS.IMPORT_SESSION_ID);
                } else {
                  // Start-From-Scratch flow - clear scratch storage
                  localStorage.removeItem(STORAGE_KEYS.COURSE_ID);
                }
                
                let newCourse = null;
                try {
                  newCourse = await courseAPI.create();
                } catch (e1) {
                  newCourse = await courseAPI.create();
                }
                
                // Set appropriate storage key based on flow
                if (propCourseData) {
                  localStorage.setItem(STORAGE_KEYS.IMPORT_COURSE_ID, newCourse.id);
                } else {
                  localStorage.setItem(STORAGE_KEYS.COURSE_ID, newCourse.id);
                }
                
                // Reset by reloading to ensure global clean state
                window.location.reload();
                } catch (error) {
                  console.error('Error restarting:', error);
                  alert(t('common.failedToRestart'));
                }
            }}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-semibold rounded-xl w-full sm:w-[380px] px-6 py-5 text-lg"
          >
            <LayoutDashboard size={18} />
            {t('step4.returnToDashboard')}
          </Button>
          <Button
            onClick={() => setExportsOpen(true)}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-semibold rounded-xl w-full sm:w-[380px] px-6 py-5 text-lg"
          >
            <Share2 size={18} />
            {t('step4.exports')}
          </Button>
        </div>

        <Dialog open={exportsOpen} onOpenChange={setExportsOpen}>
          <DialogContent className="max-w-2xl p-8">
            <DialogHeader>
              <DialogTitle className="text-2xl">{t('step4.exportOptions')}</DialogTitle>
              <DialogDescription className="text-base">{t('step4.selectFormat')}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <Button onClick={handleExportDoc} disabled={!course} data-testid="export-doc-modal" className="w-full py-4 px-6 text-base font-bold text-white rounded-lg shadow transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600">
                <span className="inline-flex items-center gap-2"><FileType2 size={20} /> {t('step4.word')}</span>
              </Button>
              <Button onClick={handleExportPdf} disabled={!course} data-testid="export-pdf-modal" className="w-full py-4 px-6 text-base font-bold text-white rounded-lg shadow transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600">
                <span className="inline-flex items-center gap-2"><FileDown size={20} /> {t('step4.pdf')}</span>
              </Button>
              <Button onClick={handleExportCdm} disabled={!course} data-testid="export-cdm-modal" className="w-full py-4 px-6 text-base font-bold text-white rounded-lg shadow transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600">
                <span className="inline-flex items-center gap-2"><Package size={20} /> .cdm</span>
              </Button>
              <Button onClick={handleExportJson} disabled={!course} data-testid="export-json-modal" className="w-full py-4 px-6 text-base font-bold text-white rounded-lg shadow transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-slate-500 to-gray-600 hover:from-slate-600 hover:to-gray-700">
                <span className="inline-flex items-center gap-2"><Braces size={20} /> .json</span>
              </Button>
              <Button onClick={handleExportEclass} disabled={!course} data-testid="export-eclass-modal" className="w-full py-4 px-6 text-base font-bold text-white rounded-lg shadow transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-orange-500 to-purple-500 hover:from-orange-600 hover:to-purple-600">
                <span className="inline-flex items-center gap-2"><GraduationCap size={20} /> {t('step4.exportForMoodle')}</span>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
